export type StorageCacheValue = string | object | number | boolean | null;
export type StorageCache = Record<string, StorageCacheValue>;

export interface StorageState {
    memoryCache: StorageCache | null;
    writeTimeoutId: ReturnType<typeof setTimeout> | null;
}

/**
 * Returns the parsed localStorage data.
 * Uses in-memory cache to avoid repeated localStorage reads.
 */
export const getParsedData = (
    state: StorageState,
    storageKey: string
): StorageCache => {
    if (state.memoryCache) return state.memoryCache;
    try {
        const raw = localStorage.getItem(storageKey);
        state.memoryCache = raw ? JSON.parse(raw) : {};
    } catch (e) {
        state.memoryCache = {};
    }
    return state.memoryCache!;
};

/**
 * Writes the in-memory cache to localStorage immediately and cancels any
 * pending debounced write. Required before hard navigations (e.g. logout)
 * so a reload cannot rehydrate stale auth from an unflushed cache.
 */
export const flushWriteSync = (
    state: StorageState,
    storageKey: string
): void => {
    if (state.writeTimeoutId) {
        clearTimeout(state.writeTimeoutId);
        state.writeTimeoutId = null;
    }
    if (state.memoryCache) {
        localStorage.setItem(storageKey, JSON.stringify(state.memoryCache));
    }
};

/**
 * Schedules a debounced write to localStorage (50ms delay).
 * Prevents multiple rapid writes — batches them into one.
 */
export const scheduleWrite = (
    state: StorageState,
    storageKey: string
): void => {
    if (state.writeTimeoutId) return;
    state.writeTimeoutId = setTimeout(() => {
        if (state.memoryCache) {
            localStorage.setItem(storageKey, JSON.stringify(state.memoryCache));
        }
        state.writeTimeoutId = null;
    }, 50);
};
